import { sha256 } from '@noble/hashes/sha256';
import { bytesToHex } from '@noble/hashes/utils';
import canonicalize from 'canonicalize';
import { ed25519 } from '@noble/curves/ed25519';
export { Result, ResultAsync, err, errAsync, fromAsyncThrowable, fromPromise, fromThrowable, ok, okAsync } from 'neverthrow';

// src/types/json.ts
var VALID_ROOT_TYPE = "object";
var VALID_NODE_TYPES = /* @__PURE__ */ new Set([
  "string",
  "number",
  "integer",
  "boolean",
  "null",
  "array",
  "object"
]);
function validateJsonSchemaShape(candidate) {
  const errors = [];
  visitRoot(candidate, errors);
  return errors;
}
function visitRoot(candidate, errors) {
  if (!isJsonObject(candidate)) {
    errors.push({
      path: "",
      code: "root-not-object",
      message: "JsonSchema root must be a JSON object"
    });
    return;
  }
  if (candidate.type !== VALID_ROOT_TYPE) {
    errors.push({
      path: "/type",
      code: "root-not-object",
      message: `JsonSchema root must declare type: 'object' \u2014 got ${JSON.stringify(candidate.type)}`
    });
    return;
  }
  visitNode(candidate, "", errors);
}
function visitNode(node, path, errors) {
  if (!isJsonObject(node)) {
    errors.push({
      path,
      code: "invalid-type-keyword",
      message: "Schema node must be a JSON object"
    });
    return;
  }
  const isEnum = "enum" in node;
  const isConst = "const" in node;
  const isCombinator = Array.isArray(node.oneOf) || Array.isArray(node.anyOf) || Array.isArray(node.allOf);
  const hasType = "type" in node;
  if (isEnum) {
    if (!Array.isArray(node.enum) || node.enum.length === 0) {
      errors.push({
        path: `${path}/enum`,
        code: "enum-empty",
        message: "enum must be a non-empty array"
      });
    }
  }
  if (isCombinator) {
    for (const key of ["oneOf", "anyOf", "allOf"]) {
      const variants = node[key];
      if (variants === void 0) continue;
      if (!Array.isArray(variants) || variants.length === 0) {
        errors.push({
          path: `${path}/${key}`,
          code: "combinator-empty",
          message: `${key} must be a non-empty array`
        });
        continue;
      }
      variants.forEach((variant, i) => {
        visitNode(variant, `${path}/${key}/${i}`, errors);
      });
    }
  }
  if (hasType) {
    const t = node.type;
    if (typeof t !== "string" || !VALID_NODE_TYPES.has(t)) {
      errors.push({
        path: `${path}/type`,
        code: "invalid-type-keyword",
        message: `type must be one of ${[...VALID_NODE_TYPES].join(", ")} \u2014 got ${JSON.stringify(t)}`
      });
      return;
    }
    if (t === "array") {
      if (node.items === void 0) {
        errors.push({
          path: `${path}/items`,
          code: "missing-items",
          message: "array schema must declare items"
        });
      } else {
        visitNode(node.items, `${path}/items`, errors);
      }
    }
    if (t === "object" && isJsonObject(node.properties)) {
      for (const [key, child] of Object.entries(node.properties)) {
        visitNode(child, `${path}/properties/${key}`, errors);
      }
    }
  } else if (!isEnum && !isConst && !isCombinator) {
    errors.push({
      path,
      code: "invalid-type-keyword",
      message: "Schema node must declare one of: type, enum, const, oneOf/anyOf/allOf"
    });
  }
}
function isJsonObject(v) {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

// src/contract/types.ts
var CONTRACT_CHECKPOINTS = [
  "pre",
  "invariants",
  "post",
  "tool_proposed"
];

// src/contract/helpers.ts
function defineContract(spec) {
  return {
    id: spec.id,
    description: spec.description,
    ...spec.citation ? { citation: spec.citation } : {},
    predicate: spec.predicate,
    severity: spec.severity ?? "block"
  };
}

// src/readiness/context.ts
function materialiseReadinessCtx(opts) {
  const { events, snapshot } = opts;
  const summaries = events.map((e) => ({
    kind: e.kind,
    timestamp: e.timestamp,
    payload: e.payload
  }));
  const consentGrants = /* @__PURE__ */ new Set();
  const consentRevokes = /* @__PURE__ */ new Set();
  for (const e of events) {
    if (e.kind === "ConsentGranted") {
      const p = e.payload.purpose;
      if (typeof p === "string") consentGrants.add(p);
    } else if (e.kind === "ConsentRevoked") {
      const p = e.payload.purpose;
      if (typeof p === "string") consentRevokes.add(p);
    }
  }
  return {
    has: (...keys) => keys.every((k) => snapshot[k] !== void 0),
    value: (key) => snapshot[key],
    consentFor: (purpose) => consentGrants.has(purpose) && !consentRevokes.has(purpose),
    events: summaries
  };
}

// src/contract/evaluate.ts
function evaluateContracts(args) {
  const { spec, intent, tenant, events, checkpoint } = args;
  switch (checkpoint) {
    case "pre":
    case "invariants":
    case "post": {
      const slot = spec.contracts?.[checkpoint] ?? [];
      if (slot.length === 0) return [];
      const ctx = buildContractCtx({ spec, intent, tenant, events });
      return slot.map((contract) => runPredicate(contract, ctx));
    }
    case "tool_proposed": {
      const slot = spec.contracts?.toolProposed ?? [];
      if (slot.length === 0) return [];
      const baseCtx = buildContractCtx({ spec, intent, tenant, events });
      const ctx = {
        ...baseCtx,
        toolName: args.toolName,
        toolArgs: args.toolArgs
      };
      return slot.map((contract) => runPredicate(contract, ctx));
    }
    default:
      return assertNever(checkpoint);
  }
}
function buildContractCtx(args) {
  const { spec, intent, tenant, events } = args;
  const readinessCtx = materialiseReadinessCtx({
    events,
    snapshot: intent.snapshot
  });
  const eventsByKind = /* @__PURE__ */ new Map();
  for (const e of events) {
    const bucket = eventsByKind.get(e.kind) ?? [];
    bucket.push(e);
    eventsByKind.set(e.kind, bucket);
  }
  return {
    intent,
    spec,
    tenant,
    events,
    snapshot: intent.snapshot,
    has: readinessCtx.has,
    value: readinessCtx.value,
    consentFor: (purpose) => readinessCtx.consentFor(purpose),
    eventsOfKind: (kind) => eventsByKind.get(kind) ?? []
  };
}
function hasBlockingFailure(results) {
  return results.some((r) => r.result === "fail" && r.severity === "block");
}
function runPredicate(contract, ctx) {
  let passed;
  try {
    passed = contract.predicate(ctx);
  } catch {
    passed = false;
  }
  const widened = contract;
  if (passed) {
    return { result: "pass", contract: widened, ctx };
  }
  return {
    result: "fail",
    contract: widened,
    ctx,
    severity: contract.severity ?? "block"
  };
}
function assertNever(value) {
  throw new Error(`unreachable ContractCheckpoint: ${String(value)}`);
}

// src/contract/normalise.ts
function normalisePredicateSource(source) {
  let out = source;
  out = out.replace(/\/\/.*$/gm, "");
  out = out.replace(/\/\*[\s\S]*?\*\//g, "");
  out = out.replace(/"([^"'\\]*(?:\\.[^"'\\]*)*)"/g, "'$1'");
  out = out.replace(/\s+/g, " ");
  out = out.trim();
  return out;
}
function hashPredicate(predicate) {
  return hashPredicateSource(predicate.toString());
}
function hashPredicateSource(source) {
  const normalised = normalisePredicateSource(source);
  const bytes = sha256(new TextEncoder().encode(normalised));
  return bytesToHex(bytes);
}

// src/contract/composition.ts
function validateComposition(parent, child) {
  const violations = [];
  const parentContracts = /* @__PURE__ */ new Map();
  for (const slot of ["pre", "invariants", "post", "toolProposed"]) {
    const slotContracts = parent.contracts?.[slot] ?? [];
    for (const c of slotContracts) {
      parentContracts.set(c.id, { contract: c, slot });
    }
  }
  const childContracts = /* @__PURE__ */ new Map();
  for (const slot of ["pre", "invariants", "post", "toolProposed"]) {
    const slotContracts = child.contracts?.[slot] ?? [];
    for (const c of slotContracts) {
      childContracts.set(c.id, c);
    }
  }
  const childDerogations = /* @__PURE__ */ new Map();
  for (const d of child.derogations ?? []) {
    childDerogations.set(d.contractId, d);
  }
  for (const [id, { contract: parentContract }] of parentContracts) {
    const childMatch = childContracts.get(id);
    const derogation = childDerogations.get(id);
    if (!childMatch && !derogation) {
      violations.push({
        code: "contract-removed",
        message: `child spec removed parent contract '${id}' without a derogation`,
        contractId: id
      });
      continue;
    }
    if (childMatch) {
      const parentSeverity = parentContract.severity ?? "block";
      const childSeverity = childMatch.severity ?? "block";
      if (parentSeverity === "block" && childSeverity === "warn") {
        violations.push({
          code: "severity-lowered",
          message: `child contract '${id}' lowered severity from 'block' to 'warn' \u2014 use a derogation instead`,
          contractId: id
        });
      }
    }
  }
  for (const derogation of child.derogations ?? []) {
    const parent2 = parentContracts.get(derogation.contractId);
    if (!parent2) {
      violations.push({
        code: "derogation-references-nonexistent-contract",
        message: `derogation references contract '${derogation.contractId}' that does not exist in parent`,
        contractId: derogation.contractId
      });
    }
    if (!derogation.basis || !derogation.basis.regulation || !derogation.basis.article) {
      violations.push({
        code: "derogation-without-basis",
        message: `derogation for '${derogation.contractId}' lacks a complete RegulationCitation basis`,
        contractId: derogation.contractId
      });
    }
    if (!derogation.justification || derogation.justification.trim().length === 0) {
      violations.push({
        code: "derogation-without-justification",
        message: `derogation for '${derogation.contractId}' lacks a justification`,
        contractId: derogation.contractId
      });
    }
  }
  return violations;
}

// src/contract/violation.ts
function makeContractViolationPayload(args) {
  const { contract, ctx, triggeringEventId, missingFields } = args;
  const intentId = ctx.intent.id;
  return {
    contractId: `${ctx.spec.key}:${contract.id}:v${ctx.intent.specVersion}`,
    contractDescription: contract.description,
    ...contract.citation ? { citation: contract.citation } : {},
    predicateHash: hashPredicate(contract.predicate),
    severity: contract.severity ?? "block",
    triggeringEventId,
    contextSummary: {
      intentId,
      snapshot: ctx.snapshot,
      ...missingFields ? { missingFields } : {}
    }
  };
}

// src/contract/violation-kind.ts
var CONTRACT_VIOLATION_KINDS = [
  "Envelope.shape.invalid",
  "Envelope.signature.invalid",
  "Bundle.hash.mismatch",
  "Chain.hash.broken",
  "Contract.predicate.unembedded",
  "Contract.predicate.retired",
  "Contract.pre.unmet",
  "Contract.inv.violated",
  "Contract.post.unmet",
  "Contract.predicate.signal-as-gate"
];

// src/contract/size-limit.ts
var PREDICATE_SIZE_LIMIT_BYTES = 4 * 1024;
function checkPredicateSize(predicate) {
  return checkPredicateSizeFromSource(predicate.toString());
}
function checkPredicateSizeFromSource(source) {
  const normalised = normalisePredicateSource(source);
  const sizeBytes = new TextEncoder().encode(normalised).byteLength;
  return {
    ok: sizeBytes <= PREDICATE_SIZE_LIMIT_BYTES,
    sizeBytes,
    limitBytes: PREDICATE_SIZE_LIMIT_BYTES
  };
}

// src/event/event-kinds.ts
var SYSTEM_EVENT_KINDS = [
  "AIProxyRefused",
  "BaselineExtracted",
  "CapturedTurn",
  "ConsentGranted",
  "ConsentRequired",
  "ConsentRevoked",
  "ContractViolation",
  "DisclosureAcknowledged",
  "DisclosureDelivered",
  "DisclosureRequired",
  "DisclosureRetracted",
  "DisclosureSignal",
  "FieldProposed",
  "FieldRejected",
  "LineageRecorded",
  "LineageRequired",
  "OversightConducted",
  "OversightEscalated",
  "OversightRequired",
  "OversightScheduled",
  "OversightSignedOff",
  "ProjectionCommit",
  "ProjectionRun",
  "RetentionExpired",
  "SourceCaptured",
  "SuggestionAccepted",
  "SuggestionEdited",
  "SuggestionRejected",
  "SuggestionSuperseded",
  "WarrantClaimed",
  "WarrantPresented",
  "WarrantViolation"
];
function isSystemEventKind(k) {
  return SYSTEM_EVENT_KINDS.includes(k);
}
function customEventKind(name) {
  return name;
}
function canonicalJSON(value) {
  assertJsonFinite(value);
  const result = canonicalize(value);
  if (typeof result !== "string") {
    throw new TypeError("canonicalJSON: value is not JSON-serialisable");
  }
  return result;
}
function assertJsonFinite(value) {
  if (typeof value === "number" && !Number.isFinite(value)) {
    throw new TypeError(
      `canonicalJSON: non-finite number (${value}) \u2014 RFC 8785 forbids NaN / Infinity`
    );
  }
  if (value === null || typeof value !== "object") return;
  if (Array.isArray(value)) {
    for (const v of value) assertJsonFinite(v);
    return;
  }
  for (const v of Object.values(value)) {
    assertJsonFinite(v);
  }
}
function isoDate(d) {
  return d.toISOString();
}
function normaliseForCanonical(value) {
  if (value === null || value === void 0) return value;
  if (value instanceof Date) return isoDate(value);
  if (Array.isArray(value)) return value.map(normaliseForCanonical);
  if (typeof value === "object") {
    const out = {};
    for (const [k, v] of Object.entries(value)) {
      out[k] = normaliseForCanonical(v);
    }
    return out;
  }
  return value;
}
function computeContentHash(event) {
  const normalised = normaliseForCanonical(event);
  const canonical = canonicalJSON(normalised);
  const bytes = sha256(new TextEncoder().encode(canonical));
  return bytesToHex(bytes);
}
function computeJsonHash(value) {
  const normalised = normaliseForCanonical(value);
  const canonical = canonicalJSON(normalised);
  const bytes = sha256(new TextEncoder().encode(canonical));
  return bytesToHex(bytes);
}
function verifyChain(events) {
  if (events.length === 0) {
    return { valid: true, brokenAt: null };
  }
  for (let i = 0; i < events.length; i++) {
    const event = events[i];
    if (event === void 0) {
      return { valid: false, brokenAt: i, reason: "event at index is undefined" };
    }
    if (i === 0) {
      if (event.prevHash !== null) {
        return {
          valid: false,
          brokenAt: 0,
          reason: "genesis event must have prevHash === null"
        };
      }
    } else {
      const prior = events[i - 1];
      if (prior === void 0) {
        return { valid: false, brokenAt: i, reason: "prior event is undefined" };
      }
      if (event.prevHash !== prior.contentHash) {
        return {
          valid: false,
          brokenAt: i,
          reason: `prevHash mismatch: expected ${prior.contentHash}, got ${event.prevHash ?? "<null>"}`
        };
      }
    }
    const { id: _id, contentHash, ...hashable } = event;
    const recomputed = computeContentHash(hashable);
    if (recomputed !== contentHash) {
      return {
        valid: false,
        brokenAt: i,
        reason: `contentHash mismatch: stored ${contentHash}, recomputed ${recomputed}`
      };
    }
  }
  return { valid: true, brokenAt: null };
}
var GENESIS_PREV_HASH = null;

// src/intent/define-crawcus-spec.ts
function defineCrawcusSpec(spec) {
  return spec;
}

// src/intent/field.ts
function makeBuilder(shape) {
  const next = (patch) => makeBuilder({ ...shape, ...patch });
  const updateMeta = (patch) => makeBuilder({ ...shape, metadata: { ...shape.metadata, ...patch } });
  const builder = {
    __field: true,
    base: shape.base,
    metadata: shape.metadata,
    ...shape.of ? { of: shape.of } : {},
    ...shape.shape ? { shape: shape.shape } : {},
    ...shape.references ? { references: shape.references } : {},
    ...shape.mime ? { mime: shape.mime } : {},
    __contracts: shape.contracts,
    required: () => updateMeta({ required: true }),
    optional: () => makeBuilder({ ...shape, metadata: { ...shape.metadata, required: false } }),
    askHint: (text) => updateMeta({ askHint: text }),
    refineHint: (text) => updateMeta({ refineHint: text }),
    label: (text) => updateMeta({ label: text }),
    help: (text) => updateMeta({ help: text }),
    placeholder: (text) => updateMeta({ placeholder: text }),
    dependsOn: (opts) => updateMeta({ dependsOn: opts }),
    askWhen: (opts) => updateMeta({ askWhen: opts }),
    validates: (fn) => updateMeta({ validates: fn }),
    defaultValue: (value) => updateMeta({ default: value }),
    options: (values) => updateMeta({ options: values }),
    confidential: () => updateMeta({ confidential: true }),
    contract: (c) => next({
      contracts: [...shape.contracts, c]
    })
  };
  return builder;
}
function emptyMetadata() {
  return { required: true };
}
var field = {
  string() {
    return makeBuilder({ base: "string", metadata: emptyMetadata(), contracts: [] });
  },
  number() {
    return makeBuilder({ base: "number", metadata: emptyMetadata(), contracts: [] });
  },
  integer() {
    return makeBuilder({ base: "integer", metadata: emptyMetadata(), contracts: [] });
  },
  boolean() {
    return makeBuilder({ base: "boolean", metadata: emptyMetadata(), contracts: [] });
  },
  date() {
    return makeBuilder({ base: "date", metadata: emptyMetadata(), contracts: [] });
  },
  datetime() {
    return makeBuilder({ base: "datetime", metadata: emptyMetadata(), contracts: [] });
  },
  enum(values) {
    return makeBuilder({
      base: "enum",
      metadata: { ...emptyMetadata(), options: values },
      contracts: []
    });
  },
  array(of) {
    return makeBuilder({
      base: "array",
      metadata: emptyMetadata(),
      of,
      contracts: []
    });
  },
  object(shape) {
    return makeBuilder({
      base: "object",
      metadata: emptyMetadata(),
      shape,
      contracts: []
    });
  },
  reference(projection) {
    return makeBuilder({
      base: "reference",
      metadata: emptyMetadata(),
      references: projection,
      contracts: []
    });
  },
  attachment(opts) {
    return makeBuilder({
      base: "attachment",
      metadata: emptyMetadata(),
      mime: opts.mime,
      contracts: []
    });
  }
};

// src/intent/extends.ts
var CLASSIFICATION_ORDER = {
  standard: 0,
  "high-risk": 1,
  prohibited: 2
};
var IntentCompositionError = class extends Error {
  constructor(message) {
    super(message);
    this.name = "IntentCompositionError";
  }
};
function composeIntent(parent, child) {
  const parentClass = parent.classification ?? "standard";
  const childClass = child.classification ?? "standard";
  if (CLASSIFICATION_ORDER[childClass] < CLASSIFICATION_ORDER[parentClass]) {
    throw new IntentCompositionError(
      `child classification '${childClass}' cannot downgrade parent classification '${parentClass}'`
    );
  }
  const mergedFields = {
    ...parent.fields,
    ...child.fields
  };
  const composedReadiness = (ctx) => {
    return parent.readiness(ctx) && child.readiness(ctx);
  };
  const contracts = {
    pre: [...parent.contracts?.pre ?? [], ...child.contracts?.pre ?? []],
    invariants: [
      ...parent.contracts?.invariants ?? [],
      ...child.contracts?.invariants ?? []
    ],
    post: [
      ...parent.contracts?.post ?? [],
      ...child.contracts?.post ?? []
    ]
  };
  const derogations = child.derogations;
  return {
    key: child.key,
    projection: child.projection,
    version: child.version,
    classification: childClass,
    fields: mergedFields,
    readiness: composedReadiness,
    ...child.customReducer ? { customReducer: child.customReducer } : parent.customReducer ? { customReducer: parent.customReducer } : {},
    ...child.i18nDefault ? { i18nDefault: child.i18nDefault } : parent.i18nDefault ? { i18nDefault: parent.i18nDefault } : {},
    ...child.tags || parent.tags ? { tags: [...parent.tags ?? [], ...child.tags ?? []] } : {},
    contracts,
    ...derogations ? { derogations } : {}
  };
}

// src/compliance/define-compliance.ts
function defineCompliance(manifest) {
  return manifest;
}

// src/compliance/field-paths.ts
function fieldPath(projection, fieldKey) {
  return `${projection}.${fieldKey}`;
}
function parseFieldPath(path) {
  const dot = path.indexOf(".");
  if (dot < 1 || dot === path.length - 1) return null;
  return {
    projection: path.slice(0, dot),
    fieldKey: path.slice(dot + 1)
  };
}

// src/compliance/validate.ts
function validateManifest(manifest, specs, opts = {}) {
  const errors = [];
  const seenKeys = /* @__PURE__ */ new Set();
  for (const spec of specs) {
    const k = spec.key;
    if (seenKeys.has(k)) {
      errors.push({
        code: "duplicate-intent-key",
        message: `intent key '${k}' is declared more than once`,
        location: { intentKey: k }
      });
    }
    seenKeys.add(k);
  }
  for (const spec of specs) {
    validateSpecFields(spec, manifest, opts, errors);
    validateSpecContracts(spec, errors);
    if (spec.extends && opts.resolveParent) {
      const parent = opts.resolveParent(spec.extends);
      if (parent) {
        const compositionViolations = validateComposition(parent, spec);
        for (const v of compositionViolations) {
          errors.push({
            code: v.code.startsWith("derogation-") ? "derogation-incomplete" : "contract-monotonicity",
            message: v.message,
            location: {
              intentKey: spec.key,
              ...v.contractId ? { contractId: v.contractId } : {}
            }
          });
        }
      }
    }
  }
  return { ok: errors.length === 0, errors };
}
function validateSpecFields(spec, manifest, opts, errors) {
  const intentKey = spec.key;
  const purpose = opts.intentPurposes?.[intentKey];
  for (const [fieldKey, fieldSpec] of Object.entries(spec.fields)) {
    const path = fieldPath(spec.projection, fieldKey);
    const fc = manifest.fields[path];
    if (!fc) {
      errors.push({
        code: "field-missing-in-manifest",
        message: `field '${path}' is referenced by intent '${intentKey}' but missing from compliance manifest`,
        location: { intentKey, fieldKey }
      });
      continue;
    }
    if (fc.pii === "special-art-9") {
      if (!hasConsentGate(fieldSpec)) {
        errors.push({
          code: "special-category-without-consent-gate",
          message: `field '${path}' is special-category (pii: 'special-art-9') but intent '${intentKey}' does not gate it on a consent event via dependsOn`,
          location: { intentKey, fieldKey }
        });
      }
    }
    if (purpose && fc.forbiddenFor?.some((p) => p === purpose)) {
      errors.push({
        code: "pii-field-with-disallowed-purpose",
        message: `field '${path}' is forbidden for purpose '${purpose}' but referenced by intent '${intentKey}' with that purpose`,
        location: { intentKey, fieldKey }
      });
    }
  }
}
function hasConsentGate(field2) {
  return field2.metadata.dependsOn !== void 0;
}
function validateSpecContracts(spec, errors) {
  const intentKey = spec.key;
  const slots = ["pre", "invariants", "post", "toolProposed"];
  for (const slot of slots) {
    const slotContracts = spec.contracts?.[slot] ?? [];
    for (const c of slotContracts) {
      const sourceCheck = checkPredicateSizeFromSource(c.predicate.toString());
      if (!sourceCheck.ok) {
        errors.push({
          code: "predicate-size-limit-exceeded",
          message: `contract '${c.id}' predicate source is ${sourceCheck.sizeBytes} bytes \u2014 exceeds ${sourceCheck.limitBytes}-byte limit. Refactor into smaller named contracts.`,
          location: { intentKey, contractId: c.id }
        });
      }
    }
  }
}

// src/readiness/check.ts
function checkReadiness(intent, spec, events) {
  const ctx = materialiseReadinessCtx({ events, snapshot: intent.snapshot });
  const ready = spec.readiness(ctx);
  if (ready) return { ready: true, missing: [] };
  const missing = [];
  for (const [key, fieldSpec] of Object.entries(spec.fields)) {
    if (fieldSpec.metadata.required && intent.snapshot[key] === void 0) {
      missing.push(key);
    }
  }
  return { ready: false, missing };
}

// src/graph/evaluate.ts
function evaluateGraph(intent, spec, events) {
  const ctx = materialiseReadinessCtx({ events, snapshot: intent.snapshot });
  const satisfied = /* @__PURE__ */ new Set();
  const pending = [];
  const blocked = [];
  for (const [key, fieldSpec] of Object.entries(spec.fields)) {
    if (intent.snapshot[key] !== void 0) {
      satisfied.add(key);
      continue;
    }
    if (!fieldSpec.metadata.required) {
      continue;
    }
    if (fieldSpec.metadata.dependsOn) {
      const askable = fieldSpec.metadata.dependsOn.when(ctx);
      if (!askable) {
        blocked.push({ key, reason: "dependsOn predicate not yet satisfied" });
        continue;
      }
    }
    pending.push({ key, priority: fieldSpec.metadata.askWhen?.priority ?? "normal" });
  }
  const priorityOrder = { early: 0, normal: 1, late: 2 };
  pending.sort((a, b) => priorityOrder[a.priority] - priorityOrder[b.priority]);
  return { satisfied, pending, blocked };
}

// src/warrant/codec.ts
function canonicalWarrantSigningBytes(warrant) {
  const { issuerSignature: _stripped, ...withoutSignature } = warrant;
  const canonical = canonicalJSON(withoutSignature);
  return new TextEncoder().encode(canonical);
}
function bytesToBase64(bytes) {
  let binary = "";
  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }
  return btoa(binary);
}
function base64ToBytes(base64) {
  try {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
  } catch {
    return null;
  }
}
function signWarrant(warrant, privateKey) {
  const stub = { ...warrant, issuerSignature: "" };
  const message = canonicalWarrantSigningBytes(stub);
  const sigBytes = ed25519.sign(message, privateKey);
  return bytesToBase64(sigBytes);
}
function verifyWarrantSignature(warrant, expectedPublicKey) {
  const sigBytes = base64ToBytes(warrant.issuerSignature);
  if (sigBytes === null || sigBytes.length !== 64) return false;
  const pubBytes = base64ToBytes(expectedPublicKey);
  if (pubBytes === null || pubBytes.length !== 32) return false;
  try {
    const message = canonicalWarrantSigningBytes(warrant);
    return ed25519.verify(sigBytes, message, pubBytes);
  } catch {
    return false;
  }
}

// src/warrant/issuer-trust.ts
function resolveTrustedPublicKey(trust, warrant) {
  const explicit = trust.roots.find((r) => r.issuerId === warrant.issuer.id);
  if (explicit !== void 0) {
    return { publicKey: explicit.publicKey, trusted: "explicit" };
  }
  if (trust.acceptUnknown) {
    return { publicKey: warrant.issuer.publicKey, trusted: "tofu" };
  }
  return null;
}

// src/warrant/evaluate.ts
function evaluateWarrant(warrant, ctx, trust, checkpoint = "pre") {
  const evaluatedAt = isoDate(ctx.now);
  function result(status, reason) {
    return reason === void 0 ? { warrantId: warrant.id, checkpoint, status, evaluatedAt } : { warrantId: warrant.id, checkpoint, status, reason, evaluatedAt };
  }
  if (warrant.revokedAt !== null) {
    return result("revoked", warrant.revocationReason ?? `revoked at ${warrant.revokedAt}`);
  }
  const issuedAt = new Date(warrant.issuedAt);
  if (ctx.now < issuedAt) {
    return result(
      "not-yet-valid",
      `Warrant.issuedAt (${warrant.issuedAt}) is in the future relative to now (${evaluatedAt})`
    );
  }
  if (warrant.expiresAt !== null) {
    const expiresAt = new Date(warrant.expiresAt);
    if (ctx.now >= expiresAt) {
      return result("expired", `Warrant expired at ${warrant.expiresAt} (now ${evaluatedAt})`);
    }
  }
  if (!warrant.scope.specs.includes(ctx.spec.key)) {
    return result(
      "out-of-scope",
      `spec ${ctx.spec.key} not in Warrant.scope.specs (${warrant.scope.specs.join(", ")})`
    );
  }
  if (warrant.scope.regions !== void 0 && warrant.scope.regions.length > 0 && !warrant.scope.regions.includes(ctx.tenant.region)) {
    return result(
      "out-of-scope",
      `tenant region ${ctx.tenant.region} not in Warrant.scope.regions (${warrant.scope.regions.join(", ")})`
    );
  }
  if (warrant.scope.classifications !== void 0 && warrant.scope.classifications.length > 0 && ctx.spec.classification !== void 0 && !warrant.scope.classifications.includes(ctx.spec.classification)) {
    return result(
      "out-of-scope",
      `spec classification ${ctx.spec.classification} not in Warrant.scope.classifications (${warrant.scope.classifications.join(", ")})`
    );
  }
  const resolved = resolveTrustedPublicKey(trust, warrant);
  if (resolved === null) {
    return result(
      "untrusted-issuer",
      `Warrant.issuer.id (${warrant.issuer.id}) not in trust roots and IssuerTrust.acceptUnknown is false`
    );
  }
  if (!verifyWarrantSignature(warrant, resolved.publicKey)) {
    return result(
      "signature-mismatch",
      `Ed25519 signature verification failed against ${resolved.trusted === "explicit" ? "explicit trust root" : "TOFU-resolved"} public key for issuer ${warrant.issuer.id}`
    );
  }
  return result("valid");
}

// src/warrant/production-trust.ts
var InsecureTrustConfigError = class extends Error {
  name = "InsecureTrustConfigError";
  /** CRAWCUS-spec-level error code (brand-neutral per ratchet #23). */
  code = "CRAWCUS_INSECURE_TRUST_CONFIG";
  constructor() {
    super(
      "IssuerTrust.acceptUnknown is true \u2014 TOFU is a dev-mode escape hatch only. Production deployments MUST configure explicit trust roots and set acceptUnknown: false. Call assertProductionTrust(config.warrants.trust) in your production bootstrap (or run with NODE_ENV=development to skip)."
    );
  }
};
function assertProductionTrust(trust) {
  if (trust.acceptUnknown) {
    throw new InsecureTrustConfigError();
  }
}
function isProductionTrust(trust) {
  return !trust.acceptUnknown;
}

// src/disclosure/evaluate.ts
function evaluateDisclosure(requirement, subject, disclosures, ctx, checkpoint = "pre") {
  const evaluatedAt = isoDate(ctx.now);
  function result(status, reason) {
    return reason === void 0 ? { requirementId: requirement.id, subject, checkpoint, status, evaluatedAt } : { requirementId: requirement.id, subject, checkpoint, status, reason, evaluatedAt };
  }
  if (requirement.recurrence === "per-session" && ctx.sessionId === void 0) {
    return result(
      "subject-missing-session",
      `Requirement '${requirement.id}' has recurrence 'per-session' but DisclosureCtx.sessionId is undefined`
    );
  }
  const forSubjectAndRequirement = disclosures.filter(
    (d) => d.subject === subject && d.requirementId === requirement.id
  );
  if (forSubjectAndRequirement.length === 0) {
    return result(
      "undelivered",
      `No Disclosure delivered to subject '${subject}' for requirement '${requirement.id}'`
    );
  }
  const mostRecent = forSubjectAndRequirement.reduce(
    (acc, d) => d.deliveredAt > acc.deliveredAt ? d : acc
  );
  if (mostRecent.retractedAt !== null) {
    return result(
      "retracted",
      `Most recent Disclosure for subject '${subject}' was retracted at ${mostRecent.retractedAt}`
    );
  }
  if (requirement.recurrence !== "once-per-subject") {
    const withinWindow = isWithinRecurrenceWindow(
      requirement.recurrence,
      mostRecent.deliveredAt,
      ctx.now
    );
    if (!withinWindow) {
      return result(
        "expired-window",
        `Most recent delivery (${mostRecent.deliveredAt}) is outside the '${requirement.recurrence}' recurrence window`
      );
    }
  }
  if (requirement.mustAcknowledge && mostRecent.acknowledgedAt === null) {
    return result(
      "unacknowledged",
      `Requirement '${requirement.id}' requires acknowledgment; most recent delivery to subject '${subject}' has not been acknowledged`
    );
  }
  return result("valid");
}
function isWithinRecurrenceWindow(recurrence, deliveredAt, now) {
  const delivered = new Date(deliveredAt);
  const elapsedMs = now.getTime() - delivered.getTime();
  if (recurrence === "annual") {
    return elapsedMs < 365 * 24 * 60 * 60 * 1e3;
  }
  if (recurrence === "per-event") {
    return false;
  }
  return true;
}

// src/disclosure/signal.ts
function disclosureHasOpportunityToBeRead(events, subject, options) {
  const acceptedSignals = options.acceptedSignals ?? ["read"];
  const requireHashMatch = options.requireHashMatch ?? true;
  const deliveries = events.filter(
    (e) => e.kind === "DisclosureDelivered"
  );
  const matchingDeliveries = deliveries.flatMap((e) => {
    const payload = e.payload;
    if (payload.subject !== subject) return [];
    if (payload.requirementId !== options.requirementId) return [];
    return [
      {
        contentHash: payload.contentHash,
        observedAt: e.timestamp.toISOString()
      }
    ];
  });
  if (matchingDeliveries.length === 0) return false;
  const mostRecentDelivery = matchingDeliveries.reduce(
    (acc, d) => d.observedAt > acc.observedAt ? d : acc
  );
  const signals = events.filter(
    (e) => e.kind === "DisclosureSignal"
  );
  const acceptedSignalSet = new Set(acceptedSignals);
  const matchingSignals = signals.filter((e) => {
    const payload = e.payload;
    if (payload.requirementId !== options.requirementId) return false;
    if (!acceptedSignalSet.has(payload.signalType)) return false;
    if (requireHashMatch && payload.contentHash !== mostRecentDelivery.contentHash) return false;
    return true;
  });
  return matchingSignals.length > 0;
}
var SIGNAL_NOT_GATE_FORBIDDEN_TOKENS = [
  "acknowledged",
  "consented",
  "agreed",
  "confirmed"
];
var SIGNAL_NOT_GATE_REQUIRED_TOKENS = ["opportunity", "signal"];
function lintDisclosureSignalPredicateName(predicateName) {
  const lower = predicateName.toLowerCase();
  for (const forbidden of SIGNAL_NOT_GATE_FORBIDDEN_TOKENS) {
    if (lower.includes(forbidden)) {
      return {
        ok: false,
        code: "FORBIDDEN_TOKEN",
        token: forbidden,
        message: `Predicate name '${predicateName}' contains forbidden token '${forbidden}'. DisclosureSignal predicates record SIGNAL (opportunity to perceive) \u2014 never GATE (affirmative acknowledgment). Use 'opportunity' or 'signal' in the name instead. See 02-product/crawcus-contracts.md \xA76.A.`
      };
    }
  }
  const hasRequired = SIGNAL_NOT_GATE_REQUIRED_TOKENS.some((token) => lower.includes(token));
  if (!hasRequired) {
    return {
      ok: false,
      code: "MISSING_REQUIRED_TOKEN",
      token: SIGNAL_NOT_GATE_REQUIRED_TOKENS.join(" | "),
      message: `Predicate name '${predicateName}' must contain 'opportunity' or 'signal' to carry the SIGNAL-not-gate claim shape. See 02-product/crawcus-contracts.md \xA76.A.`
    };
  }
  return { ok: true };
}

// src/disclosure/bundle-version.ts
function computeBundleVersion(disclosures) {
  const pairs = disclosures.map(
    (d) => [d.requirementId, computeJsonHash(toContent(d))]
  ).sort(([a], [b]) => {
    const sa = a;
    const sb = b;
    return sa < sb ? -1 : sa > sb ? 1 : 0;
  });
  return computeJsonHash(pairs);
}
function toContent(d) {
  return { text: d.text, format: d.format, locale: d.locale };
}

// src/consent/evaluate.ts
function evaluateConsent(requirement, subject, consents, ctx, checkpoint = "pre") {
  const evaluatedAt = isoDate(ctx.now);
  function result(status, reason) {
    return reason === void 0 ? { requirementId: requirement.id, subject, checkpoint, status, evaluatedAt } : { requirementId: requirement.id, subject, checkpoint, status, reason, evaluatedAt };
  }
  const forSubjectAndRequirement = consents.filter(
    (c) => c.subject === subject && c.requirementId === requirement.id
  );
  if (forSubjectAndRequirement.length === 0) {
    return result(
      "missing",
      `No Consent granted by subject '${subject}' for requirement '${requirement.id}'`
    );
  }
  const mostRecent = forSubjectAndRequirement.reduce(
    (acc, c) => c.grantedAt > acc.grantedAt ? c : acc
  );
  if (mostRecent.withdrawnAt !== null) {
    return result(
      "withdrawn",
      `Most recent Consent for subject '${subject}' was withdrawn at ${mostRecent.withdrawnAt}`
    );
  }
  if (mostRecent.regulation.regulation !== requirement.regulation.regulation || mostRecent.regulation.article !== requirement.regulation.article) {
    return result(
      "regulation-mismatch",
      `Consent regulation (${mostRecent.regulation.regulation} ${mostRecent.regulation.article}) does not match requirement (${requirement.regulation.regulation} ${requirement.regulation.article})`
    );
  }
  if (!mostRecent.purposes.includes(ctx.processingPurpose)) {
    return result(
      "purpose-out-of-scope",
      `Processing purpose '${ctx.processingPurpose}' is not in Consent.purposes (${mostRecent.purposes.join(", ")}) for subject '${subject}'`
    );
  }
  return result("valid");
}

// src/lineage/types.ts
var PROV_JSONLD_CONTEXT_URL = "https://openprovenance.org/prov-jsonld/context.jsonld";

// src/lineage/evaluate.ts
function evaluateLineage(requirement, lineages, ctx, checkpoint = "pre") {
  const evaluatedAt = isoDate(ctx.now);
  function result(status, reason) {
    return reason === void 0 ? { checkpoint, status, evaluatedAt } : { checkpoint, status, reason, evaluatedAt };
  }
  if (!ctx.hasAIProvenance) {
    return result("valid");
  }
  if (lineages.length === 0) {
    return result(
      "missing",
      `No Lineage record exists for intent '${ctx.intent.id}'; AI-mediated event requires one per spec.lineageRequirement`
    );
  }
  const mostRecent = lineages.reduce((acc, l) => l.recordedAt > acc.recordedAt ? l : acc);
  const minInputs = requirement.minInputs ?? 1;
  if (mostRecent.inputs.length < minInputs) {
    return result(
      "insufficient-inputs",
      `Lineage record has ${mostRecent.inputs.length} inputs; requirement.minInputs is ${minInputs}`
    );
  }
  const integrityCheck = checkProvOIntegrity(mostRecent.provO);
  if (integrityCheck.status !== "ok") {
    return result(integrityCheck.status, integrityCheck.reason);
  }
  return result("valid");
}
function checkProvOIntegrity(doc) {
  if (!doc["@context"]) {
    return { status: "malformed-prov-o", reason: "PROV-O document missing @context" };
  }
  if (!Array.isArray(doc["@graph"]) || doc["@graph"].length === 0) {
    return {
      status: "malformed-prov-o",
      reason: "PROV-O document @graph is empty or not an array"
    };
  }
  for (const node of doc["@graph"]) {
    if (!hasExplicitId(node)) {
      return {
        status: "blank-node-forbidden",
        reason: `PROV node lacks explicit @id (blank node forbidden per Q-CR7c federation discipline)`
      };
    }
  }
  return { status: "ok" };
}
function hasExplicitId(node) {
  return typeof node["@id"] === "string" && node["@id"].length > 0;
}

// src/oversight/evaluate.ts
function evaluateOversight(requirement, oversights, ctx, checkpoint = "pre") {
  const evaluatedAt = isoDate(ctx.now);
  function result(status, reason) {
    return reason === void 0 ? { requirementId: requirement.id, checkpoint, status, evaluatedAt } : { requirementId: requirement.id, checkpoint, status, reason, evaluatedAt };
  }
  const forRequirement = oversights.filter((o) => o.requirementId === requirement.id);
  if (forRequirement.length === 0) {
    return result("missing", `No HumanOversight record exists for requirement '${requirement.id}'`);
  }
  const mostRecent = forRequirement.reduce((acc, o) => o.conductedAt > acc.conductedAt ? o : acc);
  if (mostRecent.outcome === "escalated") {
    return result(
      "escalated",
      `Most-recent HumanOversight (${mostRecent.id}) is in 'escalated' state \u2014 remediation required before proceeding`
    );
  }
  if (!requirement.acceptedRoles.includes(mostRecent.overseer.role)) {
    return result(
      "role-not-accepted",
      `Overseer role '${mostRecent.overseer.role}' is not in requirement.acceptedRoles (${requirement.acceptedRoles.join(", ")})`
    );
  }
  const conducted = new Date(mostRecent.conductedAt);
  const elapsedMs = ctx.now.getTime() - conducted.getTime();
  const maxGapMs = requirement.maxGapDays * 24 * 60 * 60 * 1e3;
  if (elapsedMs > maxGapMs) {
    return result(
      "expired-gap",
      `Most-recent oversight conducted ${mostRecent.conductedAt} is more than ${requirement.maxGapDays} days old (now ${evaluatedAt})`
    );
  }
  return result("valid");
}

// src/tool/types.ts
var TOOL_NAME_PATTERN = /^[a-z](?:[a-z0-9]|-[a-z0-9])*$/;
var RESERVED_TOOL_NAME_PREFIXES = ["crawcus."];
var MAX_TOOL_NAME_LENGTH = 64;
function validateToolName(candidate) {
  if (candidate.length === 0) {
    return { code: "empty", message: "ToolName must not be empty" };
  }
  if (candidate.length > MAX_TOOL_NAME_LENGTH) {
    return {
      code: "too-long",
      message: `ToolName must be at most ${MAX_TOOL_NAME_LENGTH} characters`
    };
  }
  for (const prefix of RESERVED_TOOL_NAME_PREFIXES) {
    if (candidate.startsWith(prefix)) {
      return {
        code: "reserved-prefix",
        message: `ToolName prefix "${prefix}" is reserved for runtime-emitted built-in tools`
      };
    }
  }
  if (!TOOL_NAME_PATTERN.test(candidate)) {
    return {
      code: "invalid-format",
      message: "ToolName must be kebab-case: [a-z] followed by [a-z0-9-]*, no doubled hyphens, no trailing hyphen"
    };
  }
  return null;
}
function isValidToolName(candidate) {
  return validateToolName(candidate) === null;
}
var STOP_REASONS = [
  "end_turn",
  "tool_use",
  "max_tokens",
  "stop_sequence"
];
var TOOL_RISKS = ["low", "medium", "high"];
var TOOL_GATES = ["none", "contract", "human"];
var LAWFUL_BASIS_VALUES = [
  "consent",
  "contract",
  "legal-obligation",
  "vital-interests",
  "public-task",
  "legitimate-interest"
];

// src/tool/evaluate.ts
function evaluateToolSpec(name, spec) {
  const violations = [];
  if (!TOOL_RISKS.includes(spec.risk)) {
    violations.push({ code: "invalid-risk", received: String(spec.risk) });
  }
  if (!TOOL_GATES.includes(spec.gate)) {
    violations.push({ code: "invalid-gate", received: String(spec.gate) });
  }
  if (!LAWFUL_BASIS_VALUES.includes(spec.lawfulBasis)) {
    violations.push({
      code: "invalid-lawful-basis",
      received: String(spec.lawfulBasis)
    });
  }
  if (spec.inputSchema === void 0 || spec.inputSchema === null) {
    violations.push({ code: "missing-input-schema" });
  } else {
    const shapeErrors = validateJsonSchemaShape(spec.inputSchema);
    if (shapeErrors.length > 0) {
      const first = shapeErrors[0];
      if (first !== void 0) {
        violations.push({
          code: "malformed-input-schema",
          reason: `${first.path === "" ? "(root)" : first.path}: ${first.message}`
        });
      }
    }
  }
  if (violations.length === 0) {
    return { ok: true };
  }
  return { ok: false, toolName: name, violations };
}

// src/audit-bundle/types.ts
var AUDIT_BUNDLE_VERSION = "0.1.0";

export { AUDIT_BUNDLE_VERSION, CONTRACT_CHECKPOINTS, CONTRACT_VIOLATION_KINDS, GENESIS_PREV_HASH, InsecureTrustConfigError, IntentCompositionError, LAWFUL_BASIS_VALUES, MAX_TOOL_NAME_LENGTH, PREDICATE_SIZE_LIMIT_BYTES, PROV_JSONLD_CONTEXT_URL, RESERVED_TOOL_NAME_PREFIXES, SIGNAL_NOT_GATE_FORBIDDEN_TOKENS, SIGNAL_NOT_GATE_REQUIRED_TOKENS, STOP_REASONS, SYSTEM_EVENT_KINDS, TOOL_GATES, TOOL_RISKS, assertProductionTrust, base64ToBytes, buildContractCtx, bytesToBase64, canonicalJSON, canonicalWarrantSigningBytes, checkPredicateSize, checkPredicateSizeFromSource, checkProvOIntegrity, checkReadiness, composeIntent, computeBundleVersion, computeContentHash, computeJsonHash, customEventKind, defineCompliance, defineContract, defineCrawcusSpec, disclosureHasOpportunityToBeRead, evaluateConsent, evaluateContracts, evaluateDisclosure, evaluateGraph, evaluateLineage, evaluateOversight, evaluateToolSpec, evaluateWarrant, field, fieldPath, hasBlockingFailure, hashPredicate, hashPredicateSource, isProductionTrust, isSystemEventKind, isValidToolName, isWithinRecurrenceWindow, isoDate, lintDisclosureSignalPredicateName, makeContractViolationPayload, materialiseReadinessCtx, normaliseForCanonical, normalisePredicateSource, parseFieldPath, resolveTrustedPublicKey, signWarrant, validateComposition, validateJsonSchemaShape, validateManifest, validateToolName, verifyChain, verifyWarrantSignature };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map